<template>
  <el-dialog
    title="计算器"
    :visible.sync="dialogVisible"
    width="320px"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    @opened="handleOpened"
    @close="handleClose"
    class="calculator-dialog"
  >
    <div class="calculator-container">
      <!-- 显示屏 -->
      <div class="calculator-display">
        <div class="display-history">{{ history }}</div>
        <div class="display-current">{{ display }}</div>
      </div>
      
      <!-- 按钮区域 -->
      <div class="calculator-buttons">
        <!-- 第一行：清除、退格、除法 -->
        <div class="button-row">
          <button class="btn btn-function" @click="clear">C</button>
          <button class="btn btn-function" @click="clearEntry">CE</button>
          <button class="btn btn-function" @click="backspace">⌫</button>
          <button class="btn btn-operator" @click="setOperation('/')">÷</button>
        </div>
        
        <!-- 第二行：7、8、9、乘法 -->
        <div class="button-row">
          <button class="btn btn-number" @click="inputNumber('7')">7</button>
          <button class="btn btn-number" @click="inputNumber('8')">8</button>
          <button class="btn btn-number" @click="inputNumber('9')">9</button>
          <button class="btn btn-operator" @click="setOperation('*')">×</button>
        </div>
        
        <!-- 第三行：4、5、6、减法 -->
        <div class="button-row">
          <button class="btn btn-number" @click="inputNumber('4')">4</button>
          <button class="btn btn-number" @click="inputNumber('5')">5</button>
          <button class="btn btn-number" @click="inputNumber('6')">6</button>
          <button class="btn btn-operator" @click="setOperation('-')">-</button>
        </div>
        
        <!-- 第四行：1、2、3、加法 -->
        <div class="button-row">
          <button class="btn btn-number" @click="inputNumber('1')">1</button>
          <button class="btn btn-number" @click="inputNumber('2')">2</button>
          <button class="btn btn-number" @click="inputNumber('3')">3</button>
          <button class="btn btn-operator" @click="setOperation('+')">+</button>
        </div>
        
        <!-- 第五行：0、小数点、等号 -->
        <div class="button-row">
          <button class="btn btn-number btn-zero" @click="inputNumber('0')">0</button>
          <button class="btn btn-number" @click="inputDecimal">.</button>
          <button class="btn btn-equals" @click="calculate">=</button>
        </div>
      </div>
    </div>
    
    <!-- <div slot="footer" class="dialog-footer"> -->
      <!-- <el-button @click="handleClose">关闭</el-button> -->
      <!-- <el-button type="primary" @click="copyResult">复制结果</el-button> -->
    <!-- </div> -->
  </el-dialog>
</template>

<script>
import NP from 'number-precision'

export default {
  name: 'CalculatorDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      display: '0',           // 当前显示的数字
      history: '',            // 历史记录显示
      previousValue: null,    // 上一个操作数
      operation: null,        // 当前操作符
      waitingForOperand: false, // 是否等待新的操作数
      justCalculated: false   // 是否刚完成计算
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.resetCalculator()
          // 弹窗打开时添加键盘事件监听，使用捕获阶段确保优先处理
          this.$nextTick(() => {
            document.addEventListener('keydown', this.handleKeydown, true)
          })
        } else {
          // 弹窗关闭时移除键盘事件监听
          document.removeEventListener('keydown', this.handleKeydown, true)
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    // 组件销毁时确保移除键盘事件监听
    document.removeEventListener('keydown', this.handleKeydown, true)
  },
  methods: {
    // 弹窗打开事件
    handleOpened() {
      this.$emit('opened')
      // 聚焦到弹窗容器以接收键盘事件
      this.$nextTick(() => {
        const dialog = this.$el.querySelector('.el-dialog')
        if (dialog) {
          dialog.setAttribute('tabindex', '-1')
          dialog.focus()
        }
      })
    },
    
    // 弹窗关闭事件
    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
    },
    
    // 重置计算器
    resetCalculator() {
      this.display = '0'
      this.history = ''
      this.previousValue = null
      this.operation = null
      this.waitingForOperand = false
      this.justCalculated = false
    },
    
    // 输入数字
    inputNumber(num) {
      if (this.waitingForOperand || this.justCalculated) {
        this.display = num
        this.waitingForOperand = false
        this.justCalculated = false
      } else {
        this.display = this.display === '0' ? num : this.display + num
      }
    },
    
    // 输入小数点
    inputDecimal() {
      if (this.waitingForOperand || this.justCalculated) {
        this.display = '0.'
        this.waitingForOperand = false
        this.justCalculated = false
      } else if (this.display.indexOf('.') === -1) {
        this.display += '.'
      }
    },
    
    // 设置操作符
    setOperation(nextOperation) {
      const inputValue = parseFloat(this.display)
      
      if (this.previousValue === null) {
        this.previousValue = inputValue
      } else if (this.operation) {
        const currentValue = this.previousValue || 0
        const newValue = this.performCalculation(currentValue, inputValue, this.operation)
        
        this.display = String(newValue)
        this.previousValue = newValue
      }
      
      this.waitingForOperand = true
      this.operation = nextOperation
      this.justCalculated = false
      
      // 更新历史记录
      this.updateHistory()
    },
    
    // 执行计算
    performCalculation(firstValue, secondValue, operation) {
      try {
        switch (operation) {
          case '+':
            return NP.plus(firstValue, secondValue)
          case '-':
            return NP.minus(firstValue, secondValue)
          case '*':
            return NP.times(firstValue, secondValue)
          case '/':
            if (secondValue === 0) {
              this.$message.error('不能除以零')
              return firstValue
            }
            return NP.divide(firstValue, secondValue)
          default:
            return secondValue
        }
      } catch (error) {
        this.$message.error('计算错误')
        return 0
      }
    },
    
    // 计算结果
    calculate() {
      const inputValue = parseFloat(this.display)
      
      if (this.previousValue !== null && this.operation) {
        const newValue = this.performCalculation(this.previousValue, inputValue, this.operation)
        
        // 更新历史记录显示完整计算过程
        this.history = `${this.previousValue} ${this.getOperatorSymbol(this.operation)} ${inputValue} =`
        this.display = String(newValue)
        this.previousValue = null
        this.operation = null
        this.waitingForOperand = true
        this.justCalculated = true
      }
    },
    
    // 获取操作符显示符号
    getOperatorSymbol(op) {
      const symbols = {
        '+': '+',
        '-': '-',
        '*': '×',
        '/': '÷'
      }
      return symbols[op] || op
    },
    
    // 更新历史记录
    updateHistory() {
      if (this.previousValue !== null && this.operation) {
        this.history = `${this.previousValue} ${this.getOperatorSymbol(this.operation)}`
      }
    },
    
    // 清除所有
    clear() {
      this.resetCalculator()
    },
    
    // 清除当前输入
    clearEntry() {
      this.display = '0'
    },
    
    // 退格
    backspace() {
      if (this.display.length > 1) {
        this.display = this.display.slice(0, -1)
      } else {
        this.display = '0'
      }
    },
    
    // 复制结果
    copyResult() {
      try {
        // 创建临时文本区域
        const textArea = document.createElement('textarea')
        textArea.value = this.display
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        
        this.$message.success('结果已复制到剪贴板')
      } catch (error) {
        this.$message.error('复制失败')
      }
    },
    
    // 键盘事件处理
    handleKeydown(event) {
      // 只有在弹窗显示时才处理键盘事件
      if (!this.visible) {
        return
      }
      
      // 立即阻止事件传播，防止其他组件处理
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      
      const key = event.key
      // 数字键
      if (/^[0-9]$/.test(key)) {
        this.inputNumber(key)
      }
      // 小数点
      else if (key === '.') {
        this.inputDecimal()
      }
      // 操作符
      else if (key === '+') {
        this.setOperation('+')
      } else if (key === '-') {
        this.setOperation('-')
      } else if (key === '*') {
        this.setOperation('*')
      } else if (key === '/') {
        this.setOperation('/')
      }
      // 等号或回车
      else if (key === '=' || key === 'Enter') {
        this.calculate()
      }
      // 退格
      else if (key === 'Backspace') {
        this.backspace()
      }
      // 清除
      else if (key === 'Delete' || key === 'c' || key === 'C') {
        this.clear()
      }
      // ESC关闭
      else if (key === 'Escape') {
        this.handleClose()
      }
    }
  },

}
</script>

<style scoped>
.calculator-dialog {
  user-select: none;
}

.calculator-container {
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
}

.calculator-display {
  background: #000;
  color: #fff;
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 5px;
  text-align: right;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.display-history {
  font-size: 14px;
  color: #888;
  min-height: 20px;
  opacity: 0.8;
}

.display-current {
  font-size: 24px;
  font-weight: bold;
  min-height: 30px;
  word-break: break-all;
}

.calculator-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.button-row {
  display: flex;
  gap: 8px;
}

.btn {
  flex: 1;
  height: 50px;
  border: none;
  border-radius: 5px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.btn-number {
  background: #fff;
  color: #333;
  border: 1px solid #ddd;
}

.btn-number:hover {
  background: #f0f0f0;
}

.btn-operator {
  background: #ff9500;
  color: #fff;
}

.btn-operator:hover {
  background: #e6850e;
}

.btn-function {
  background: #a6a6a6;
  color: #fff;
}

.btn-function:hover {
  background: #8f8f8f;
}

.btn-equals {
  background: #ff9500;
  color: #fff;
  flex: 2;
}

.btn-equals:hover {
  background: #e6850e;
}

.btn-zero {
  flex: 2;
}

.dialog-footer {
  text-align: right;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

/* 响应式设计 */
@media (max-width: 400px) {
  .calculator-container {
    padding: 5px;
  }
  
  .btn {
    height: 45px;
    font-size: 16px;
  }
  
  .display-current {
    font-size: 20px;
  }
}
</style>
